/**
 * The client's own booking access tokens, per studio, on this device only.
 * A token opens exactly one booking; it is never put in a URL or sent to the
 * service worker cache.
 */
import { readJson, writeJson } from './storage';

export interface SavedBooking { token: string; id: string; starts_at: string; service_name: string; saved_at: string }

const key = (slug: string) => `studio:${slug}:my-bookings`;

export function listSaved(slug: string): SavedBooking[] {
  return readJson<SavedBooking[]>(key(slug), []).filter((b) => typeof b.token === 'string');
}

export function saveBooking(slug: string, b: Omit<SavedBooking, 'saved_at'>): void {
  const rest = listSaved(slug).filter((x) => x.id !== b.id);
  writeJson(key(slug), [{ ...b, saved_at: new Date().toISOString() }, ...rest].slice(0, 20));
}

export function forgetBooking(slug: string, id: string): void {
  writeJson(key(slug), listSaved(slug).filter((x) => x.id !== id));
}

/**
 * One idempotency key per booking attempt. It survives a reload or a lost
 * response, so pressing "Подтвердить" again re-sends the same key and the
 * server returns the same booking and the same access token.
 */
export function attemptKey(slug: string, fingerprint: string): string {
  const k = `studio:${slug}:attempt:${fingerprint}`;
  const existing = readJson<string | null>(k, null, 'session');
  if (existing) return existing;
  const bytes = crypto.getRandomValues(new Uint8Array(18));
  const fresh = btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  writeJson(k, fresh, 'session');
  return fresh;
}

export function clearAttempt(slug: string, fingerprint: string): void {
  try { window.sessionStorage.removeItem(`studio:${slug}:attempt:${fingerprint}`); } catch { /* ignore */ }
}
